import React, { useEffect, useRef } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

const Result = ({
  correctCount = 0,
  wrongCount = 0,
  totalQuestions = 12,
  restartQuiz,
  nextLevel,
  level,
  hasNextLevel,
  mode = "cultura", // 👈 novo: modo pode ser 'cultura' ou 'ecologia'
}) => {
  const percentage = (correctCount / totalQuestions) * 100;
  const navigate = useNavigate();
  const passed = percentage >= 50;
  const auth = getAuth();
  const user = auth.currentUser;
  const hasSavedScore = useRef(false);

  const saveScoreToRanking = async () => {
    if (!user || hasSavedScore.current) return;

    try {
      const collectionName = mode === "ecologia" ? "rankingEcologia" : "ranking"; // 👈 coleção baseada no modo
      const userRef = collection(db, collectionName);
      const q = query(userRef, where("email", "==", user.email));
      const querySnapshot = await getDocs(q);

      const newScore = correctCount * 10;
      let totalScore = newScore;
      let highestLevel = level;

      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        const existingData = querySnapshot.docs[0].data();
        const previousScore = existingData.scores?.[level] || 0;
        const levelScore = Math.max(previousScore, newScore);

        const scores = { ...existingData.scores, [level]: levelScore };
        totalScore = Object.values(scores).reduce((acc, curr) => acc + curr, 0);
        highestLevel = Math.max(level, existingData.level);

        await setDoc(
          docRef,
          {
            totalScore,
            level: highestLevel,
            scores,
            date: serverTimestamp(),
          },
          { merge: true }
        );
      } else {
        await setDoc(doc(db, collectionName, user.uid), {
          name: user.displayName || "Jogador Anônimo",
          email: user.email,
          totalScore,
          level: highestLevel,
          scores: { [level]: newScore },
          date: serverTimestamp(),
        });
      }

      hasSavedScore.current = true;
    } catch (error) {
      console.error("❌ Erro ao salvar pontuação:", error);
    }
  };

  useEffect(() => {
    if (
      correctCount + wrongCount === totalQuestions &&
      !hasSavedScore.current
    ) {
      saveScoreToRanking();
    }
  }, [correctCount, wrongCount, totalQuestions]);

  return (
    <motion.div
      style={styles.pageStyle}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div style={styles.overlayStyle}></div>
      <div style={styles.containerStyle}>
        <h1 style={styles.titleStyle}>
          {passed
            ? `Parabéns! Você passou para o Nível ${level + 1}!`
            : "Tente novamente!"}
        </h1>

        <motion.div
          style={styles.scoreStyle}
          initial={{ rotate: -180 }}
          animate={{ rotate: 0 }}
          transition={{ duration: 1 }}
        >
          <CircularProgressbar
            value={percentage}
            text={`${percentage.toFixed(1)}%`}
            styles={buildStyles({
              textColor: passed ? "#388E3C" : "#D32F2F",
              pathColor: passed ? "#388E3C" : "#D32F2F",
              trailColor: "#ddd",
              textSize: "16px",
            })}
          />
        </motion.div>

        <p style={styles.textStyle}>Respostas certas: {correctCount}</p>
        <p style={styles.textStyle}>Respostas erradas: {wrongCount}</p>

        {passed && (
          <motion.button
            style={{
              ...styles.buttonStyle,
              background: hasNextLevel ? "#4CAF50" : "#888",
              cursor: hasNextLevel ? "pointer" : "not-allowed",
            }}
            whileHover={hasNextLevel ? { scale: 1.1 } : {}}
            whileTap={hasNextLevel ? { scale: 0.9 } : {}}
            onClick={hasNextLevel ? nextLevel : null}
            disabled={!hasNextLevel}
          >
            Próximo Nível
          </motion.button>
        )}

        {!passed && (
          <motion.button
            style={{ ...styles.buttonStyle, background: "#D32F2F" }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={restartQuiz}
          >
            Tentar Novamente
          </motion.button>
        )}

        <motion.button
          style={{
            ...styles.buttonStyle,
            background: "#007BFF",
            marginLeft: "10px",
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => navigate("/")}
        >
          Voltar ao Início
        </motion.button>
      </div>
    </motion.div>
  );
};

const styles = {
  pageStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundImage: "url('/7.jpg')",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  overlayStyle: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
  },
  containerStyle: {
    position: "relative",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    padding: "30px",
    borderRadius: "15px",
    boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.3)",
    textAlign: "center",
    width: "90%",
    maxWidth: "500px",
  },
  titleStyle: {
    fontSize: "2rem",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  scoreStyle: {
    margin: "20px 0",
    width: "150px",
    height: "150px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  textStyle: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    marginTop: "10px",
    color: "#333",
  },
  buttonStyle: {
    marginTop: "15px",
    padding: "12px 25px",
    fontSize: "1.2rem",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "0.3s ease-in-out",
    fontWeight: "bold",
    textTransform: "uppercase",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
  },
};

export default Result;
